import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { App } from '../app';
import { SelectOption } from '../../../shared/select-input/select-input.component';
import { YelpZapierSetup, YelpZapierSetupService, YelpZapierZap } from './yelp-zapier-setup.service';

const ZAPIER_ORIGIN = 'https://zapier.com';
const YELP_POLL_INTERVAL_MS = 5000;
const YELP_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const YELP_ZAP_COUNT = 3;
const ZAP_TRANSLATION_KEYS: Record<string, string> = {
  new_lead: 'APP.YELP.SETUP.ZAP_NEW_LEAD',
  new_consumer_message: 'APP.YELP.SETUP.ZAP_NEW_CONSUMER_MESSAGE',
  reply: 'APP.YELP.SETUP.ZAP_REPLY',
};

@Component({
  selector: 'rosie-yelp-zapier-setup',
  templateUrl: './yelp-zapier-setup.component.html',
  styleUrls: ['./yelp-zapier-setup.component.scss'],
  standalone: false,
})
export class YelpZapierSetupComponent implements OnInit, OnDestroy {
  @Input() app: App;
  @Input() preview = false;
  @Output() setupCompleteChange = new EventEmitter<boolean>();

  setup: YelpZapierSetup = null;
  isLoading = true;
  isConnectingZapier = false;
  isWaitingForYelp = false;
  isLoadingBusinesses = false;
  isTurningOn = false;
  showManualSetup = false;
  copiedApiKey = false;

  yelpAccountControl = new UntypedFormControl(null);
  yelpBusinessControl = new UntypedFormControl(null);
  yelpAccountOptions: SelectOption[] = [];
  yelpBusinessOptions: SelectOption[] = [];

  private yelpPopup: Window = null;
  private yelpPollTimer: ReturnType<typeof setInterval> = null;
  private yelpPollStartedAt = 0;

  constructor(
    private yelpZapierSetupService: YelpZapierSetupService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    if (this.preview) {
      this.isLoading = false;
      return;
    }

    this.yelpAccountControl.valueChanges.subscribe((yelpAuthenticationId) => this.loadYelpBusinesses(yelpAuthenticationId));
    this.refresh();
  }

  ngOnDestroy(): void {
    this.stopWaitingForYelp();
  }

  get isZapierConnected(): boolean {
    return !!this.setup?.zapierConnected;
  }

  get hasYelpAccount(): boolean {
    return (this.setup?.yelpAccounts?.length ?? 0) > 0;
  }

  get selectedYelpAccountTitle(): string {
    return this.setup?.yelpAccounts?.find((account) => account.id === this.yelpAccountControl.value)?.title ?? '';
  }

  get zaps(): YelpZapierZap[] {
    return this.setup?.zaps ?? [];
  }

  get areAllZapsOn(): boolean {
    return this.zaps.length === YELP_ZAP_COUNT && this.zaps.every((zap) => zap.isEnabled);
  }

  get canTurnOn(): boolean {
    if (!this.yelpAccountControl.value || this.isTurningOn || this.isLoadingBusinesses) {
      return false;
    }

    return this.yelpBusinessOptions.length <= 1 || !!this.yelpBusinessControl.value;
  }

  get turnOnTranslationKey(): string {
    return this.zaps.length > 0 ? 'APP.YELP.SETUP.TURN_ON_AGAIN' : 'APP.YELP.SETUP.TURN_ON';
  }

  zapTranslationKey(zap: YelpZapierZap): string {
    return ZAP_TRANSLATION_KEYS[zap.key] ?? ZAP_TRANSLATION_KEYS.new_consumer_message;
  }

  connectZapier(): void {
    this.isConnectingZapier = true;

    this.yelpZapierSetupService.authorizationUrl().subscribe({
      next: (url) => window.location.href = url,
      error: (error) => {
        this.isConnectingZapier = false;
        this.showError(error);
      },
    });
  }

  disconnectZapier(): void {
    this.yelpZapierSetupService.disconnect().subscribe({
      next: () => this.refresh(),
      error: (error) => this.showError(error),
    });
  }

  connectYelp(): void {
    if (!this.setup?.yelpConnectUrl) {
      return;
    }

    this.yelpPopup = window.open(this.setup.yelpConnectUrl, 'rosie-zapier-yelp', 'width=600,height=720');
    this.startWaitingForYelp();
  }

  turnOn(): void {
    this.isTurningOn = true;

    this.yelpZapierSetupService.turnOn(this.yelpAccountControl.value, this.yelpBusinessControl.value).subscribe({
      next: (setup) => {
        this.isTurningOn = false;
        this.applySetup(setup);
      },
      error: (error) => {
        this.isTurningOn = false;
        this.showError(error);
      },
    });
  }

  openZap(zap: YelpZapierZap): void {
    window.open(zap.editorUrl, '_blank', 'noopener');
  }

  openYelpZap(): void {
    window.open(this.app?.config?.zapUrl, '_blank', 'noopener');
  }

  copyApiKey(): void {
    navigator.clipboard.writeText(this.app?.config?.apiKey).then(() => {
      this.copiedApiKey = true;
      setTimeout(() => this.copiedApiKey = false, 2000);
    });
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (event.origin !== ZAPIER_ORIGIN || event.data?.type !== 'zapier.popup.close') {
      return;
    }

    this.stopWaitingForYelp();
    this.refresh();
  }

  private refresh(): void {
    this.yelpZapierSetupService.get().subscribe({
      next: (setup) => {
        this.isLoading = false;
        this.applySetup(setup);
      },
      error: (error) => {
        this.isLoading = false;
        this.showError(error);
      },
    });
  }

  private applySetup(setup: YelpZapierSetup): void {
    this.setup = setup;
    this.setupCompleteChange.emit(this.areAllZapsOn || !!this.app?.config?.lastMessageOn);
    this.yelpAccountOptions = (setup?.yelpAccounts ?? []).map((account) => ({ value: account.id, label: account.title }));

    const accountIds = this.yelpAccountOptions.map((option) => option.value);
    const preferredAccountId = [this.yelpAccountControl.value, setup?.yelpAuthenticationId, accountIds[0]]
      .find((id) => !!id && accountIds.includes(id)) ?? null;

    if (preferredAccountId !== this.yelpAccountControl.value) {
      this.yelpAccountControl.setValue(preferredAccountId);
    }

    if (this.isWaitingForYelp && accountIds.length > 0) {
      this.stopWaitingForYelp();
    }
  }

  private loadYelpBusinesses(yelpAuthenticationId: string): void {
    this.yelpBusinessOptions = [];
    this.yelpBusinessControl.setValue(null);

    if (!yelpAuthenticationId) {
      return;
    }

    this.isLoadingBusinesses = true;

    this.yelpZapierSetupService.yelpBusinesses(yelpAuthenticationId).subscribe({
      next: (businesses) => {
        this.isLoadingBusinesses = false;
        this.yelpBusinessOptions = businesses.map((business) => ({ value: business.id, label: business.name }));

        const businessIds = businesses.map((business) => business.id);
        const preferredBusinessId = [this.setup?.yelpBusinessId, businessIds.length === 1 ? businessIds[0] : null]
          .find((id) => !!id && businessIds.includes(id)) ?? null;

        this.yelpBusinessControl.setValue(preferredBusinessId);
      },
      error: (error) => {
        this.isLoadingBusinesses = false;
        this.showError(error);
      },
    });
  }

  private startWaitingForYelp(): void {
    this.stopWaitingForYelp();
    this.isWaitingForYelp = true;
    this.yelpPollStartedAt = Date.now();

    this.yelpPollTimer = setInterval(() => {
      const popupClosed = !this.yelpPopup || this.yelpPopup.closed;
      const timedOut = Date.now() - this.yelpPollStartedAt > YELP_POLL_TIMEOUT_MS;

      if (popupClosed || timedOut) {
        this.stopWaitingForYelp();
      }

      this.refresh();
    }, YELP_POLL_INTERVAL_MS);
  }

  private stopWaitingForYelp(): void {
    this.isWaitingForYelp = false;

    if (this.yelpPollTimer) {
      clearInterval(this.yelpPollTimer);
      this.yelpPollTimer = null;
    }
  }

  private showError(error: any): void {
    this.messageService.add({
      severity: 'error',
      detail: (typeof error === 'string' && error) || error?.message || 'Something went wrong. Try again.',
      life: 8000,
    });
  }
}
